export type PreferenceScope = 'device' | 'account'
export type PreferenceType = 'enum' | 'boolean' | 'route'
export type PreferenceSection = 'appearance' | 'defaults'
export type PreferenceValue = string | boolean

export interface PreferenceOption {
  value: string
  label: string
}

export interface PreferenceEntry {
  key: string
  label: string
  description: string
  scope: PreferenceScope
  type: PreferenceType
  options?: readonly PreferenceOption[]
  default: PreferenceValue
  section: PreferenceSection
  /** Rendered but non-interactive this slice (e.g. Theme, pending real dark mode). */
  disabled?: boolean
}

// The single declarative source of truth for preference shape. Adding a preference
// later is one entry here, not a new form. Device-scoped entries persist to
// localStorage (UI state only); account-scoped entries are declared INERT this
// slice (A6 §7 — they need a backend table/endpoint before they can switch on).
export const PREFERENCE_REGISTRY = [
  {
    key: 'appearance.theme',
    label: 'Theme',
    description: 'Light, dark, or match your system.',
    scope: 'device',
    type: 'enum',
    options: [
      { value: 'light', label: 'Light' },
      { value: 'dark', label: 'Dark' },
      { value: 'system', label: 'System' },
    ],
    default: 'system',
    section: 'appearance',
  },
  {
    key: 'appearance.density',
    label: 'Density',
    description: 'Row height for tables and lists. Applies with a mouse or trackpad.',
    scope: 'device',
    type: 'enum',
    options: [
      { value: 'comfortable', label: 'Comfortable' },
      { value: 'compact', label: 'Compact' },
    ],
    default: 'comfortable',
    section: 'appearance',
  },
  {
    key: 'appearance.reduceMotion',
    label: 'Reduce motion',
    description: 'Minimize animations and transitions. Your system setting always wins.',
    scope: 'device',
    type: 'boolean',
    default: false,
    section: 'appearance',
  },
  {
    key: 'defaults.landingPage',
    label: 'Landing page',
    description: 'Where the app opens after you sign in.',
    scope: 'account',
    type: 'route',
    default: '/dashboard',
    section: 'defaults',
  },
  {
    key: 'defaults.listRefreshStrategy',
    label: 'List refresh',
    description: 'How dynamic lists refresh their members.',
    scope: 'account',
    type: 'enum',
    options: [
      { value: 'manual', label: 'Manual' },
      { value: 'scheduled', label: 'Scheduled' },
    ],
    default: 'manual',
    section: 'defaults',
  },
  {
    key: 'defaults.dateFormat',
    label: 'Date format',
    description: 'How dates are displayed across the app.',
    scope: 'account',
    type: 'enum',
    options: [
      { value: 'short', label: 'Short' },
      { value: 'medium', label: 'Medium' },
      { value: 'long', label: 'Long' },
    ],
    default: 'medium',
    section: 'defaults',
  },
] as const satisfies readonly PreferenceEntry[]

export type PreferenceKey = (typeof PREFERENCE_REGISTRY)[number]['key']

export function getPreferenceEntry(key: PreferenceKey): PreferenceEntry {
  const entry = PREFERENCE_REGISTRY.find((e) => e.key === key)
  if (!entry) throw new Error(`Unknown preference key: ${key}`)
  return entry
}

export function appearancePreferences(): PreferenceEntry[] {
  return PREFERENCE_REGISTRY.filter((e) => e.section === 'appearance')
}
